import React, {useCallback, useState} from 'react';

import {addGenericErrorToast} from '@albert-io/atomic';
import {refreshSession} from '@albert-io/mandark/authentication';
import {type UserModelV2} from 'resources/augmented/User/UserModel.v2';
import ContentPersonalization from 'client/AccountSettings/ContentPersonalization/ContentPersonalization.react';
import {redirect} from 'client/User/UserRedirectUtil';

import {useCurrentUserPendingTasksQuery} from '../../signUp.queries.generated';
import {usePerformSignupStep} from '../../usePerformSignupStep';

import {SIGNUP_TASKS} from '../onboarding.types';
import type {PersonalInfoData, SchoolData} from '../onboarding.types';

import PersonalInfoStep from './PersonalInfoStep';
import SchoolAssociationStep from './SchoolAssociationStep';

type EducatorOnboardingStep = 'personal-info' | 'school-association' | 'content-personalization';

interface NewEducatorOnboardingProps {
  user: UserModelV2;
  onComplete: () => void;
  onFinalStepComplete: () => void;
}

function getInitialStep(pendingTasks: string[]): EducatorOnboardingStep {
  if (pendingTasks.includes(SIGNUP_TASKS.ACCOUNT_COMPLETION)) {
    return 'personal-info';
  }
  if (pendingTasks.includes(SIGNUP_TASKS.CONTENT_PERSONALIZATION)) {
    return 'content-personalization';
  }
  return 'personal-info';
}

export default function NewEducatorOnboarding({user, onComplete}: NewEducatorOnboardingProps) {
  const {data: pendingData, loading} = useCurrentUserPendingTasksQuery();
  const performSignupStep = usePerformSignupStep();

  const pendingTasks = pendingData?.currentUser?.signupPending ?? [];
  const initialStep = loading ? 'personal-info' : getInitialStep(pendingTasks);

  const [currentStep, setCurrentStep] = useState<EducatorOnboardingStep | null>(null);
  const [personalInfo, setPersonalInfo] = useState<PersonalInfoData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeStep = currentStep ?? initialStep;

  const navigateToStep = useCallback((step: EducatorOnboardingStep) => {
    window.history.replaceState({educatorOnboardingStep: step}, '');
    setCurrentStep(step);
  }, []);

  const handlePersonalInfoContinue = (data: PersonalInfoData) => {
    setPersonalInfo(data);
    navigateToStep('school-association');
  };

  const handleSchoolContinue = async (schoolData: SchoolData) => {
    if (!personalInfo) return;
    setIsSubmitting(true);

    try {
      // If user selected homeschool, create/join the school first.
      if (schoolData.homeschool) {
        await performSignupStep({
          homeschoolSchoolSetup: {
            action: schoolData.homeschool.action,
            name: schoolData.homeschool.name,
            city: schoolData.homeschool.city,
            state: schoolData.homeschool.state,
            country: schoolData.homeschool.country,
            ...(schoolData.homeschool.action === 'join' && schoolData.homeschool.schoolId
              ? {schoolId: schoolData.homeschool.schoolId}
              : {})
          }
        });
      }
      // Complete account completion with personal info + school
      await performSignupStep({
        accountCompletion: {
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
          username: personalInfo.username,
          salutation: personalInfo.title,
          schoolId: schoolData.schoolId
        }
      });
      onComplete();
      navigateToStep('content-personalization');
    } catch {
      addGenericErrorToast();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContentPersonalizationComplete = async () => {
    await refreshSession();
    await redirect();
  };

  if (loading) return null;

  switch (activeStep) {
    case 'personal-info':
      return (
        <PersonalInfoStep
          onContinue={handlePersonalInfoContinue}
          // Pre-fill from the user record — populated when the educator signed up via
          // Clever or Google SSO (BE captures first/last name in the OAuth callback).
          defaultFirstName={user.getFirstName?.() ?? ''}
          defaultLastName={user.getLastName?.() ?? ''}
        />
      );

    case 'school-association':
      return (
        <SchoolAssociationStep
          onContinue={handleSchoolContinue}
          isSubmitting={isSubmitting}
          lastName={personalInfo?.lastName}
        />
      );

    case 'content-personalization':
      return (
        <ContentPersonalization isOnboarding onComplete={handleContentPersonalizationComplete} />
      );

    default:
      return null;
  }
}
